/**
 * Browser-side API client.
 *
 * The translate call is Server-Sent Events over `fetch`, not `EventSource`:
 * EventSource can only issue GET requests, and the input text belongs in a body
 * rather than in a URL (length limits, and it would land in every access log).
 * So we POST and parse the SSE framing ourselves — it is a dozen lines.
 */

import { currentToken, refreshToken, adoptToken } from './originToken';
import { DEFAULT_COMPANY, companyOf } from './company';
import type { Role } from './roles';
import { beginLeaveWait } from './wait';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  /** Up to 3 ISO-3166 codes; rendered as flags in the picker. */
  countries: string[];
}

export interface Mode {
  id: string;
  label: string;
  group: 'translate' | 'learn';
  hint: string;
  /** Icon name; components/ModeIcons.tsx maps it to an SVG. */
  icon: string | null;
  /** Pins the target AND locks the picker. */
  forcesTarget: string | null;
  /**
   * Languages the mode imposes server-side WITHOUT changing the language bar —
   * the UI must not lock or repoint the pickers for these.
   */
  assumesLanguages: { from?: string; to?: string } | null;
  /**
   * The answer comes back in the language the user WROTE IN — true for every
   * mode that rewrites text inside one language (A1, Registers, Humanize,
   * Mistakes) rather than translating between two.
   *
   * The UI acts on it twice: it hides the target tabs, the target dropdown and
   * the swap button (there is no second language to choose), and it fills the
   * `{lang}` placeholder in `label` and `hint` with the input language's name.
   */
  followsInputLanguage: boolean;
  /**
   * The mode produces its own teaching section instead of the one the Overview
   * switch adds. The notes card titles itself from this — the CEFR modes'
   * section is a glossary of the ORIGINAL wording, not notes about the answer.
   */
  writesOwnNotes: boolean;
  /**
   * The mode has nothing to do when both languages match, so the browser can
   * answer it without asking. True for faithful translation only — see the
   * field's note in the backend's lib/modes.ts for why it is not simply
   * `from === to`.
   */
  identityWhenSameLanguage: boolean;
  /**
   * Each sentence of this mode's answer depends only on that sentence, so the
   * browser may hold the finished ones and ask for the new one alone. True for
   * faithful translation only — see the field's note in the backend's
   * lib/modes.ts for why a Humanize or Mistakes answer cannot work this way.
   */
  incremental: boolean;
}

/** Same-origin in production; nginx routes /api to the backend. */
const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

export async function fetchLanguages(): Promise<Language[]> {
  const res = await fetch(`${API}/api/languages`);
  if (!res.ok) throw new Error('could not load languages');
  return (await res.json()).languages;
}

export async function fetchModes(): Promise<{
  modes: Mode[];
  default: string;
  /**
   * The server's per-request character ceiling, which is what the input box
   * pages at. Optional so an older backend simply leaves the client on its own
   * default rather than paginating at `undefined`.
   */
  maxInputChars?: number;
  /**
   * The AI Prompt tabs, in order, as names for the ids that arrive inside the
   * answer's `[[STYLE:…]]` tags.
   *
   * Optional, and the pane copes without it: an unknown id is drawn as a tab
   * titled from the id itself. That matters because these are operator-editable
   * — a style added in the console while someone has the page open would
   * otherwise be a tab with no name.
   */
  promptStyles?: { styleId: string; label: string }[];
  /**
   * How the input box behaves, set by the operator in the console.
   *
   * `typingCountdownSeconds` is the wait the pane counts down after somebody
   * stops typing; `livePreviewMs` is the debounce on the live draft shown while
   * they are still going, with 0 meaning no draft at all.
   *
   * Both optional, and the component keeps its own defaults for the case an
   * older backend sends neither — the same arrangement `maxInputChars` has, for
   * the same reason: a frontend that assumed the field was there would show an
   * `undefined`-second countdown against a backend one deploy behind.
   */
  typingCountdownSeconds?: number;
  livePreviewMs?: number;
}> {
  const res = await fetch(`${API}/api/modes`);
  if (!res.ok) throw new Error('could not load modes');
  return res.json();
}

export interface TranslateHandlers {
  /** Fires once, immediately — carries the resolved (or still-pending) source language. */
  onMeta?: (m: {
    from: string;
    fromName: string | null;
    detected: boolean;
    detecting: boolean;
    to: string;
    toName: string;
    mode: string;
  }) => void;
  /** Fires when parallel detection resolves, which may be after some text has arrived. */
  onDetected?: (d: { from: string; fromName: string; confidence: number }) => void;
  onDelta?: (chunk: string) => void;
  /**
   * `kharma` is what this answer cost the account (settled from what the model
   * really used, under the engine's level pricing; 0 for a cache hit, the same
   * language or a guest), `balance` the wallet after it, `level` the level it
   * ran at.
   */
  onDone?: (d: { text: string; ms: number; cached: boolean; model: string; kharma?: number; balance?: number; level?: string }) => void;
  /**
   * `status` is the HTTP status when the server answered and refused, and
   * undefined when there was no answer to read one from — a dropped connection,
   * a socket that never opened. The caller needs the difference: a 400 is the
   * server saying THIS REQUEST is malformed, which is worth retrying in another
   * shape, while a dropped connection says nothing about the request at all.
   */
  onError?: (message: string, status?: number) => void;
  /** Quota exhausted — a different shape of problem from a failure. */
  onLimit?: (message: string, retryAfterSec: number | null) => void;
}

export interface TranslateInput {
  text: string;
  from: string;
  to: string;
  mode: string;
  /** Ask for the one-sentence overview alongside the answer. */
  overview?: boolean;
  /**
   * `text` holds only the lines still needing a translation, and `context` is
   * the passage they came from with those lines marked. The answer is exactly
   * as many lines as `text` has.
   */
  partial?: boolean;
  context?: string;
  /**
   * AI Prompt only: write just this one style.
   *
   * The mode answers in tabs, and it used to write every tab in a single call —
   * so four of the five versions were paid for and never read. The browser now
   * asks for one when its tab is clicked. Omitted by every other caller, and
   * omitting it still means "all of them".
   */
  style?: string;
  /**
   * Registers only: write just this one of the three.
   *
   * The same change `style` above describes, for the mode that had the same
   * problem in a sharper form: formal, friendly and slang were all written on
   * every request, and each of them is a full rewrite of the whole message, so
   * two thirds of a long paste was paid for and never opened.
   *
   * Omitted by every other caller, and omitting it still means all three.
   */
  register?: 'formal' | 'friendly' | 'casual';
  /**
   * Translate only: ask for the AI rendering rather than the faithful one.
   *
   * The Translate pane shows the two as tabs, Standard and AI. The faithful one
   * is the ordinary answer and comes through the normal path; this is the second
   * tab, asked for when it is opened. Omitted everywhere else, and omitting it is
   * the faithful tab — so nothing that predates this sends anything different.
   */
  ai?: boolean;
  /** Translate only: the Easy tab — the text taken down to A1, then translated. */
  easy?: boolean;
  /**
   * The translation level (Low · Medium · High · Advanced · Expert), passed
   * EXPLICITLY by the caller that owns a level picker — the translator, at each
   * of its call sites. Omitted, no level is sent and the server uses its own
   * default (the first level; a guest always gets the guest level).
   *
   * There used to be a module-wide level set by the translator during render,
   * and every other caller inherited it — so a Camps post translated on a page
   * the translator had set to Expert was charged at Expert. A level is now
   * only ever what the call itself says.
   */
  level?: string | null;
  signal?: AbortSignal;
}

/**
 * Fired when the server refused a level it does not know (the engine's level
 * list changed under an open tab). The translator listens and re-reads
 * GET /api/engine; the request itself is retried once without a level.
 */
export const ENGINE_STALE_EVENT = 'gt:engine-stale';

/**
 * Re-read the wallet a moment after a stream was cut short.
 *
 * A charged request takes a HOLD before the stream opens, and the header shows
 * the wallet after it (`X-Marq-Balance`). The settled balance only arrives with
 * `done` — so a stream the browser aborted (a newer keystroke, a closed tab, a
 * page turn) left the header showing the post-hold value until the next spend.
 * The server settles a cancelled hold when it notices the closed socket, so the
 * read waits a little, and a burst of aborts (typing at the first level) makes
 * ONE read rather than one per keystroke.
 */
let balanceResync: ReturnType<typeof setTimeout> | null = null;
function resyncBalanceSoon(): void {
  if (typeof window === 'undefined') return;
  if (balanceResync) clearTimeout(balanceResync);
  balanceResync = setTimeout(() => {
    balanceResync = null;
    void fetchMarq().catch(() => undefined);
  }, 2000);
}

/* ══ The browser's sign-in: HttpOnly JWT cookies, nothing stored here ═════════
 *
 * (owner, 2026-10-12 — backend lib/auth.ts, docs/AUTH_JWT_PROMPT.md)
 *
 * Signing in sends the nickname and password ONCE. The server answers with two
 * HttpOnly cookies page scripts cannot read: `gt_at`, a 15-minute JWT access
 * token, and `gt_rt`, a refresh token (30 days sliding with "Keep me signed
 * in", the browser session without; rotated on every use, 180 days at most).
 * An expired access token is renewed by the server on the next /api request,
 * and on a 401 this file calls POST /api/auth/refresh once and retries.
 *
 * Nothing secret lives in localStorage any more — only the last nickname, to
 * pre-fill the sign-in form. Two OLD copies are traded in once and erased:
 *   · `gt_login` — the nickname + PASSWORD browsers kept until 2026-10-12;
 *   · `gt_token` — the long-lived token dev browsers kept for a few hours.
 */
/** The OLD stores, read only to migrate them away. Never written again. */
const LEGACY_TOKEN_STORE = 'gt_token';
const LEGACY_TOKEN_OFF = 'gt_token_off';
const LEGACY_LOGIN_STORE = 'gt_login';
/** The nickname this browser last signed in with (not a secret). */
const NICK_STORE = 'gt_last_nick';

export interface StoredSignIn { token: string; nickname: string }

/** The legacy stored token, if one is still waiting to be migrated. */
export function readToken(): StoredSignIn | null {
  try {
    const v = JSON.parse(localStorage.getItem(LEGACY_TOKEN_STORE) || 'null');
    return v && typeof v.token === 'string' && v.token ? { token: v.token, nickname: typeof v.nickname === 'string' ? v.nickname : '' } : null;
  } catch { return null; }
}
/** "Keep me signed in" is now a server-side choice (refresh-cookie lifetime); only the old flag is cleaned up. */
export function setKeepSignedIn(_on: boolean): void {
  try { localStorage.removeItem(LEGACY_TOKEN_OFF); } catch { /* */ }
}
/** Remember the NICKNAME (for the form) and erase any legacy token. The token argument is ignored. */
export function rememberToken(_token: string | null | undefined, nickname: string): void {
  try {
    if (nickname) localStorage.setItem(NICK_STORE, nickname);
    localStorage.removeItem(LEGACY_TOKEN_STORE);
  } catch { /* storage disabled: nothing to do */ }
}
function dropToken(): void { try { localStorage.removeItem(LEGACY_TOKEN_STORE); } catch { /* */ } }
/** Signing out: erase every legacy copy (the nickname pre-fill stays). */
export function forgetToken(): void {
  try { localStorage.removeItem(LEGACY_TOKEN_STORE); localStorage.removeItem(LEGACY_LOGIN_STORE); localStorage.removeItem(LEGACY_TOKEN_OFF); } catch { /* */ }
}
/** The nickname this browser last signed in with — the login form pre-fills it (only it). */
export function rememberedNickname(): string {
  try { const n = localStorage.getItem(NICK_STORE); if (n) return n; } catch { /* */ }
  const t = readToken(); if (t && t.nickname) return t.nickname;
  try { const v = JSON.parse(localStorage.getItem(LEGACY_LOGIN_STORE) || 'null'); return v && typeof v.nickname === 'string' ? v.nickname : ''; } catch { return ''; }
}
/** Only for the one-time migration of a legacy stored token (the server converts it into cookies). */
function authHeaders(): Record<string, string> {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t.token}` } : {};
}

/**
 * The one-time trade of an old stored PASSWORD for a cookie sign-in.
 *
 *  · signed in already: the password copy is simply erased;
 *  · not signed in: the password is sent ONE last time and erased whatever the
 *    answer — except when the server could not be reached (kept for next time,
 *    or somebody whose network blinked would be signed out).
 *
 * Resolves true when this browser is signed in afterwards.
 */
let migrating: Promise<boolean> | null = null;
export function migrateLegacyLogin(signedIn: boolean): Promise<boolean> {
  if (migrating) return migrating;
  migrating = (async () => {
    let saved: { nickname?: unknown; password?: unknown } | null = null;
    try { saved = JSON.parse(localStorage.getItem(LEGACY_LOGIN_STORE) || 'null'); } catch { saved = null; }
    const drop = () => { try { localStorage.removeItem(LEGACY_LOGIN_STORE); } catch { /* */ } };
    if (!saved) return signedIn;
    if (signedIn || typeof saved.nickname !== 'string' || typeof saved.password !== 'string' || !saved.nickname || !saved.password) { drop(); return signedIn; }
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ nickname: saved.nickname, password: saved.password, remember: true }),
      });
      if (res.status >= 500 || res.status === 429) return false;   // keep it: try again next visit
      drop();
      if (!res.ok) return false;
      const body = await res.json().catch(() => ({}));
      rememberToken(null, body.user?.nickname || saved.nickname);
      return true;
    } catch {
      return false;   // unreachable: keep it for the next visit
    }
  })();
  void migrating.finally(() => { migrating = null; });
  return migrating;
}

/** One recovery at a time. A page can fail four gated requests at once. */
let recovering: Promise<boolean> | null = null;

/**
 * Sign back in silently after a 401: the refresh cookie first (POST
 * /api/auth/refresh rotates it and sets a new access cookie), then a legacy
 * stored token, then a legacy stored password. Must not touch React state.
 *
 * False means nothing works any more — only then does anybody see a form.
 */
function recoverSession(): Promise<boolean> {
  if (recovering) return recovering;

  recovering = (async () => {
    try {
      const r = await fetch(`${API}/api/auth/refresh`, { method: 'POST', credentials: 'include', cache: 'no-store' });
      if (r.ok) return true;
      if (r.status >= 500) return false;   // unreachable: NOT a signed-out user
    } catch {
      return false;
    }
    const saved = readToken();
    if (saved) {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: 'include', headers: authHeaders(), cache: 'no-store' });
        if (!res.ok) return false;
        const body = await res.json().catch(() => ({}));
        dropToken();   // converted into cookies (or dead): either way never needed again
        if (body.user) { rememberToken(null, body.user.nickname); return true; }
      } catch {
        return false;
      }
    }
    return migrateLegacyLogin(false);
  })();

  void recovering.finally(() => { recovering = null; });
  return recovering;

}

// ── Guests: free translations before sign-up (backend lib/guestCredits.ts) ──

/** Opens the sign-up prompt. detail.reason: 'limit' | 'feature'. */
export const GUEST_PROMPT_EVENT = 'gt:guest-prompt';
/** The server's latest count of free translations left. detail: number. */
export const GUEST_REMAINING_EVENT = 'gt:guest-remaining';

/** Set by AuthProvider: no account, and the server allows guests. */
let guestModeOn = false;
export function setGuestMode(on: boolean): void {
  guestModeOn = on;
}

function announceGuestRemaining(n: number): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(GUEST_REMAINING_EVENT, { detail: n }));
}

/**
 * The server's latest KHARMA balance, pushed out of whichever call just spent
 * some. detail: number.
 *
 * Same shape as GUEST_REMAINING_EVENT above and for the same reason: the
 * header chrome shows a number that only the DATA calls ever learn about, and
 * threading a setter down through the translator, the voice dialog and the
 * image dialog would mean four components owning a fact none of them is about.
 * Every spend already answers with `X-Marq-Balance`; this carries it.
 */
export const MARQ_BALANCE_EVENT = 'gt:marq-balance';

/**
 * A spend was refused for want of KHARMA. detail: MarqShortfall.
 *
 * Separate from the balance event because the two mean different things to the
 * chrome: one updates a number, the other asks the daily-reward button to make
 * itself impossible to miss.
 */
export const MARQ_EMPTY_EVENT = 'gt:marq-empty';

function announceMarqBalance(n: number): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MARQ_BALANCE_EVENT, { detail: n }));
}

/** Pull `X-Marq-Balance` off any response that carries one. */
function readMarqHeader(res: Response): void {
  const left = res.headers.get('X-Marq-Balance');
  if (left !== null && Number.isFinite(Number(left))) announceMarqBalance(Number(left));
}

/** What the server sends when an account has run out of KHARMA. */
export interface MarqShortfall {
  cost: number;
  balance: number;
  canClaimDaily: boolean;
  nextDailyAt: string;
}

/**
 * Out of credit — thrown instead of a plain Error so the UI can offer the daily
 * reward rather than printing a sentence and stopping.
 *
 * It carries the server's own numbers rather than re-deriving them: whether the
 * reward is claimable right now is a question only the server can answer (it
 * owns the UTC clock), and a browser guessing from a cached wallet would offer
 * a button that then does nothing.
 */
export class MarqEmptyError extends Error {
  readonly marq: MarqShortfall;
  constructor(message: string, marq: MarqShortfall) {
    super(message);
    this.name = 'MarqEmptyError';
    this.marq = marq;
  }
}

/** Is this the server saying "this account has no KHARMA left"? */
function isMarqEmpty(res: Response, body: any): boolean {
  // 402 Payment Required, matched on the code for the reason isSignedOut gives.
  return res.status === 402 && body?.code === 'marq-empty';
}

/** Build the error for a 402, with a fallback for a body that arrived empty. */
function marqEmptyError(body: any): MarqEmptyError {
  const m = body?.marq ?? {};
  return new MarqEmptyError(
    body?.error || 'You have run out of KHARMA.',
    {
      cost: Number(m.cost) || 0,
      balance: Number(m.balance) || 0,
      canClaimDaily: !!m.canClaimDaily,
      nextDailyAt: String(m.nextDailyAt || ''),
    },
  );
}

function isGuestLimit(res: Response, body: any): boolean {
  return res.status === 401 && body?.code === 'guest-limit';
}

/** Is this the server saying "nobody is signed in"? */
function isSignedOut(res: Response, body: any): boolean {
  // Matched on the CODE rather than the sentence: `middleware/requireAuth.ts`
  // sends `{ error, code: 'signin-required' }` with a 401, and matching on the
  // English would break the first time the wording changed.
  return res.status === 401 && body?.code === 'signin-required';
}

/**
 * The server has refused this request because nobody is signed in — so put it
 * right, and only fall back to the login page if it cannot be put right.
 *
 * ── Why this exists when AuthGate already redirects ─────────────────────────
 * That gate runs when the TRANSLATOR PAGE LOADS, and it is right about who is
 * signed in at that moment. This is about the moment afterwards: a cookie that
 * expires while a tab sits open, a session signed out in another tab, a server
 * that no longer recognises the token. In every one of those the page is still
 * on screen, still looks usable, and the next Ctrl+Enter used to come back as a
 * red line of error text under a translator the reader could not use.
 *
 * ── Why it repairs before it redirects ──────────────────────────────────────
 * Because being sent to a login form is the thing this product promised would
 * not happen. It used to navigate on the first 401, which meant a tab left open
 * over a weekend threw away whatever was in the box and showed a password
 * field to somebody whose password this browser was holding the whole time.
 * Now the credentials are replayed first and the caller simply tries again;
 * the form is what is left for a browser that has nothing stored, or stored
 * something that no longer works.
 *
 * `window.location.assign` rather than the Next router: this module is imported
 * by client components but is not one itself, and a hard navigation is also the
 * honest thing to do at that point — every piece of state on the page belongs
 * to a session that really has ended.
 *
 * Returns 'retry' when the session is back and the request is worth sending
 * again, 'gone' when the caller should report its own error, and 'no' when this
 * was never a sign-in failure at all.
 */
async function recoverOrRedirect(res: Response, body: any): Promise<'retry' | 'gone' | 'no'> {
  if (typeof window === 'undefined') return 'no';
  // A guest who has used their free translations. Not a redirect: the page
  // stays, the count goes to 0, and the sign-up prompt takes it from there.
  if (isGuestLimit(res, body)) {
    announceGuestRemaining(0);
    return 'gone';
  }
  if (!isSignedOut(res, body)) return 'no';

  if (await recoverSession()) return 'retry';

  // A guest (no account, free translations on) reaching an account-only
  // feature — voice, image, feedback. Ask them to sign up instead of throwing
  // away the page they are using.
  if (guestModeOn) {
    window.dispatchEvent(new CustomEvent(GUEST_PROMPT_EVENT, { detail: { reason: 'feature' } }));
    return 'gone';
  }

  // Already on the way there. Without this a page holding several gated
  // requests — the translation, the usage bar, the pronunciation card — would
  // each start their own navigation as they failed in turn.
  if (window.location.pathname !== '/login') { beginLeaveWait('/login'); window.location.assign('/login'); }
  return 'gone';
}

/**
 * A gated JSON request, with the sign-in repair above applied once.
 *
 * Every route under `requireUser` fails the same way when a cookie lapses, and
 * every caller of one wants the same three things: send it, replay the stored
 * credentials if the server says nobody is signed in, send it again. Written
 * out per endpoint that is fifteen lines each and one of them always forgets
 * the retry; written here it is the default.
 *
 * The retry is bounded at one by construction — the second attempt does not
 * come back through this branch — so a server that answers 401 to everything
 * cannot put a page into a loop.
 */
export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const send = () =>
    fetch(`${API}${path}`, {
      credentials: 'include',
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init?.headers ?? {}),
      },
    });

  let res = await send();
  if (!res.ok) {
    let body: any = {};
    try { body = await res.clone().json(); } catch { /* an error page, not JSON */ }
    const recovered = await recoverOrRedirect(res, body);
    if (recovered === 'retry') res = await send();
    else if (recovered === 'gone') throw new Error('Please sign in.');
  }

  readMarqHeader(res);

  let body: any = {};
  try { body = await res.json(); } catch { /* 204, or an error page */ }
  if (!res.ok) {
    // Out of credit is its own error type — the caller can offer the reward.
    if (isMarqEmpty(res, body)) throw marqEmptyError(body);
    throw new Error(body?.error || `That did not work (${res.status})`);
  }
  return body as T;
}

// ── The KHARMA wallet ─────────────────────────────────────────────────────────

export interface DailyRewardStatus {
  day: string; serverNow: string; resetAt: string; base: number; bonus: number; cycle: number;
  streak: number; bestStreak: number; todayDay: number; todayAmount: number;
  claimedToday: boolean; lastAmount: number | null; lastBase?: number | null; lastBonus?: number | null; historyPending: boolean;
}
export interface DailyClaimResult {
  claimed: boolean; amount: number; base?: number; bonus?: number; streak?: number; wallet: MarqWallet;
}
export interface RewardSummary {
  wallet: MarqWallet; since: string; totals: Record<string, { amount: number; count: number }>;
}
export const fetchRewardSummary = () => apiJson<RewardSummary>('/api/marq/rewards');

export interface MarqWallet {
  daily?: DailyRewardStatus;
  balance: number;
  canClaimDaily: boolean;
  nextDailyAt: string;
  dailyAmount: number;
  earned: number;
  spent: number;
  enabled: boolean;
}

export interface MarqEntry {
  id?: string;
  amount: number;
  kind: 'signup' | 'daily' | 'spend' | 'refund' | 'admin' | 'reward' | 'match_escrow' | 'match_win' | 'match_refund' | 'season_prize' | 'upvote' | 'award_give' | 'award_get' | 'downvote' | 'learn';
  action: string;
  balanceAfter: number;
  note: string;
  at: string;
}

/** The current wallet. Used to refresh after a spend, not on first paint —
 *  `/auth/me` already carries it, so the header draws without a second call. */
export async function fetchMarq(announce = true): Promise<MarqWallet> {
  const body = await apiJson<{ wallet: MarqWallet }>('/api/marq');
  if (announce) announceMarqBalance(body.wallet.balance);
  return body.wallet;
}

/**
 * Claim today's reward.
 *
 * `claimed: false` is a NORMAL answer, not a failure — it means somebody
 * already took it today, possibly in another tab. The server returns 200 for
 * it deliberately; see routes/marq.ts.
 */
export async function claimDailyMarq(): Promise<DailyClaimResult> {
  const body = await apiJson<DailyClaimResult>(
    '/api/marq/daily', { method: 'POST' },
  );
  return body;
}

/** Recent movements, newest first — the Credit tab's list. */
export async function fetchMarqHistory(limit = 20): Promise<MarqEntry[]> {
  const body = await apiJson<{ entries: MarqEntry[] }>(`/api/marq/history?limit=${limit}`);
  return body.entries ?? [];
}

// ── Relationships ────────────────────────────────────────────────────────────

/** One person this account has played online co-op with (backend lib/relationship.ts). */
export interface RelationshipRow {
  userId: string;
  nickname: string;
  team: number | null;
  company: string;
  avatar: string | null;
  score: number;
  tier: string;
  games: number;
  clears: number;
  fails: number;
  lastAt: string | null;
  lastGame: string;
  lastDelta: number;
}

/** Settings ▸ Relationship: everyone the caller has played with, highest score first. */
export async function fetchRelationships(): Promise<{ rows: RelationshipRow[]; rules: { clear: number; fail: number; dailyGainCap: number } }> {
  return apiJson(`/api/relationships`);
}

// ── Premium games ────────────────────────────────────────────────────────────

/** One premium game's catalogue entry. The price is the server's, not ours. */
export interface PremiumGameInfo {
  price: number;
  /** KHARMA earnable by clearing every mission — the draw to buy. */
  reward: number;
  missions: number;
  pitch: string;
}

/**
 * The premium catalogue and which of them this account owns.
 *
 * Works signed out too (the server answers `owned: []`), so the shelf can grey
 * the locked ones for a guest and still show prices. Games not in `games` are
 * free.
 */
export async function fetchPremiumGames(): Promise<{
  enabled: boolean;
  games: Record<string, PremiumGameInfo>;
  owned: string[];
}> {
  return apiJson('/api/marq/games');
}

/**
 * Buy a premium game. On success the balance header refreshes the header coin
 * (via apiJson → readMarqHeader) and the returned `owned` unlocks the shelf.
 * Throws with the server's message on 402 (not enough KHARMA) or 401 (signed out).
 */
export async function buyGame(
  game: string,
  nonce: string,
): Promise<{ ok: true; game: string; price: number; balance: number; owned: string[]; duplicate?: boolean }> {
  return apiJson('/api/marq/buy-game', { method: 'POST', body: JSON.stringify({ game, nonce }) });
}

export async function translateStream(
  input: TranslateInput,
  handlers: TranslateHandlers,
): Promise<void> {
  /** The level on the wire; dropped for the one retry after an "unknown level". */
  let level = input.level || null;
  const post = (token: string | null) =>
    fetch(`${API}/api/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Proof this came from the page rather than a script.
        ...(token ? { 'x-gt-token': token } : {}),
      },
      // The anonymous session cookie has to ride along: it is half of what the
      // token is bound to, and history/stats key off it too.
      credentials: 'same-origin',
      body: JSON.stringify({
        text: input.text,
        from: input.from,
        to: input.to,
        mode: input.mode,
        overview: !!input.overview,
        // A partial request: `text` is the missing lines and `context` is what
        // they sit in. Both, or neither — the backend refuses a partial request
        // with no context rather than quietly translating a fragment blind.
        ...(input.partial ? { partial: true, context: input.context ?? '' } : {}),
        // Only when there is one. An absent `style` is the whole set, which is
        // what every other mode and every older client sends.
        ...(input.style ? { style: input.style } : {}),
        // The same rule for the register: sent only when one was named, and its
        // absence still means all three.
        ...(input.register ? { register: input.register } : {}),
        // And the same rule again for the Easy tab: sent only when that tab
        // asked, and its absence is the faithful translation.
        ...(input.ai ? { ai: true } : input.easy ? { easy: true } : {}),
        ...(level ? { level } : {}),
      }),
      signal: input.signal,
    });

  let res: Response;
  try {
    res = await post(currentToken() ?? (await refreshToken()));

    // 403 means the token was missing, expired or bound to a stale session.
    // Mint a new one and retry ONCE — that covers a tab left open past the
    // token's lifetime, without letting a genuine rejection loop.
    if (res.status === 403) {
      const fresh = await refreshToken();
      if (fresh) res = await post(fresh);
    }

    /*
     * 401 means the SIGN-IN lapsed underneath this tab, and the same rule
     * applies: sign back in from what the browser stored and retry ONCE.
     *
     * Done here rather than left to the failure handling below because this is
     * the one call on the page where giving up costs the reader something — the
     * text in the box, and the wait they have already sat through. Everywhere
     * else a recovered session simply means the next request works.
     *
     * The body is read off a clone so the real response is still intact for the
     * error path, which needs to read it too when there is nothing to recover.
     */
    if (res.status === 401) {
      const peek = await res.clone().json().catch(() => ({} as any));
      if (isSignedOut(res, peek) && (await recoverSession())) {
        res = await post(currentToken() ?? (await refreshToken()));
      }
    }

    /*
     * 400 "unknown level": the engine's level list changed while this tab was
     * open (an operator renamed or removed one). Not the reader's mistake, and
     * not worth an error line — ask the page to re-read the engine, and ask
     * again ONCE with no level, which the server answers at its default.
     */
    if (res.status === 400 && level) {
      const peek = await res.clone().json().catch(() => ({} as any));
      if (/unknown level/i.test(String(peek?.error ?? ''))) {
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ENGINE_STALE_EVENT));
        level = null;
        res = await post(currentToken() ?? (await refreshToken()));
      }
    }
  } catch (err) {
    // An abort is us cancelling a superseded request, not a failure to report.
    if ((err as Error).name === 'AbortError') return;
    handlers.onError?.('Could not reach the server.');
    return;
  }

  /** A hold was taken for this request (the header carries the wallet after it). */
  const held = res.ok && res.headers.get('X-Marq-Balance') !== null;
  // A guest's translation: the server says how many free ones are left.
  const guestLeft = res.headers.get('X-Guest-Remaining');
  if (guestLeft !== null && Number.isFinite(Number(guestLeft))) announceGuestRemaining(Number(guestLeft));
  readMarqHeader(res);

  if (!res.ok) {
    const detail = await res.json().catch(() => ({} as any));
    // A guest out of free translations: the prompt explains, so no error line.
    // The pane still has to stop its spinner, so it gets the sentence too.
    if (isGuestLimit(res, detail) && typeof window !== 'undefined') {
      announceGuestRemaining(0);
      window.dispatchEvent(new CustomEvent(GUEST_PROMPT_EVENT, { detail: { reason: 'limit' } }));
      handlers.onError?.(detail?.error ?? 'Create a free account to keep translating.');
      return;
    }
    /*
     * Out of KHARMA. Announced as its own event rather than only as an error
     * line, because the answer is a BUTTON — the daily reward, two inches away
     * in the header — and a sentence in the output pane cannot point at it.
     * The pane still gets the sentence so its spinner stops; the same division
     * the guest prompt above already uses.
     */
    if (isMarqEmpty(res, detail) && typeof window !== 'undefined') {
      const err = marqEmptyError(detail);
      announceMarqBalance(err.marq.balance);
      window.dispatchEvent(new CustomEvent(MARQ_EMPTY_EVENT, { detail: err.marq }));
      handlers.onError?.(shortfallMessage(err, !!level));
      return;
    }
    // Before anything is reported to the caller: there is no error to show
    // somebody who is about to be on another page.
    if ((await recoverOrRedirect(res, detail)) !== 'no') return;
    if (res.status === 429) {
      const retry = Number(res.headers.get('Retry-After'));
      handlers.onLimit?.(
        detail?.error ?? 'You have reached the translation limit.',
        Number.isFinite(retry) ? retry : null,
      );
      return;
    }
    handlers.onError?.(detail?.error ?? 'Translation failed.', res.status);
    return;
  }
  if (!res.body) {
    handlers.onError?.('Translation failed.');
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  /**
   * Whether the server said its last word — `done` or `error`.
   *
   * A stream can also just END: a proxy timeout, a backend restart, a dropped
   * mobile connection. Saying nothing then left the translator believing the
   * request was still on its way, so it kept its spinner and, worse, kept the
   * question marked as in flight — and every later attempt at the same text,
   * a second paste included, was skipped as "already being asked". So the end
   * of a stream without a terminal event is reported as the failure it is.
   */
  let finished = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (!finished && !input.signal?.aborted) handlers.onError?.('The connection dropped mid-translation.');
        if (!finished && held) resyncBalanceSoon();
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      // SSE messages are separated by a blank line. Anything after the last
      // separator is a partial message and stays in the buffer.
      const messages = buffer.split('\n\n');
      buffer = messages.pop() ?? '';

      for (const message of messages) {
        let event = 'message';
        let data = '';
        for (const line of message.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data += line.slice(5).trim();
        }
        if (!data) continue;

        let payload: any;
        try {
          payload = JSON.parse(data);
        } catch {
          continue;
        }

        if (event === 'meta') handlers.onMeta?.(payload);
        else if (event === 'detected') handlers.onDetected?.(payload);
        else if (event === 'delta') handlers.onDelta?.(payload.t);
        else if (event === 'done') {
          finished = true;
          adoptToken(payload.token);   // rotate before the next request
          // The wallet after the settled price (the header carried it after the hold).
          if (Number.isFinite(payload.balance)) announceMarqBalance(Number(payload.balance));
          handlers.onDone?.(payload);
        }
        else if (event === 'error') { finished = true; handlers.onError?.(payload.message); }
      }
    }
  } catch (err) {
    // Cut short with a hold taken: the header still shows the post-hold wallet.
    if (!finished && held) resyncBalanceSoon();
    if ((err as Error).name === 'AbortError') return;
    handlers.onError?.('The connection dropped mid-translation.');
  }
}

/**
 * The sentence for a translation refused for want of KHARMA.
 *
 * Under level pricing the server holds up to about twice the estimate while the
 * answer is written (and charges only what it used), so the amount that has to
 * be in the wallet is the HOLD, not the "≈ N" the picker shows. The 402 carries
 * that hold as `marq.cost`; this says it plainly, with the wallet beside it, so
 * "≈ 25" next to "you need 50" is not a contradiction nobody explained.
 */
function shortfallMessage(err: MarqEmptyError, leveled: boolean): string {
  const { cost, balance, canClaimDaily } = err.marq;
  if (!cost) return err.message;
  const tail = canClaimDaily
    ? 'Claim your daily reward to keep going.'
    : 'Your next daily reward unlocks soon.';
  return leveled
    ? `This needs ${cost.toLocaleString()} KHARMA in your wallet to start (you have ${balance.toLocaleString()}). ` +
      'It is held while the answer is written, and you pay only what it really uses. ' + tail
    : `You need ${cost.toLocaleString()} KHARMA for this (you have ${balance.toLocaleString()}). ` + tail;
}

/* ── Voice files ────────────────────────────────────────────────────────────
 *
 * Two calls, because the work outlives the request that starts it: the upload
 * hands the bytes over and gets a job id, and a server-sent stream follows that
 * job to its transcript. See the backend's lib/transcribe.ts for why.
 */

export interface AudioLimits {
  enabled: boolean;
  maxBytes: number;
  maxSeconds: number;
  maxChars: number;
  minutesPerDay: number;
  formats: string[];
  /** Whether the mic button's fallback path is available — see MicButton.tsx. */
  live?: { enabled: boolean; maxSeconds: number };
}

export async function fetchAudioLimits(signal?: AbortSignal): Promise<AudioLimits> {
  const res = await fetch(`${API}/api/transcribe/limits`, { signal, credentials: 'include' });
  if (!res.ok) throw new Error('limits unavailable');
  return res.json();
}

export interface TranscribeProgress {
  stage: 'queued' | 'reading' | 'transcribing' | 'done' | 'error';
  done: number;
  total: number;
  text: string;
  seconds: number;
  error?: string;
  truncated?: boolean;
}

/**
 * Send the file. Resolves with the job id as soon as the bytes are accepted.
 *
 * XHR rather than fetch, for one reason that fetch still cannot do: upload
 * progress. A 40 MB voice memo over a phone connection is most of the wait, and
 * `xhr.upload.onprogress` is the only way to show it moving. (`fetch` with a
 * ReadableStream request body exists, but needs HTTP/2 and duplex support that
 * Safari does not have.)
 */
export function uploadAudio(
  file: File,
  onUploadProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<{ id: string; seconds: number; chunks: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/transcribe`);
    xhr.withCredentials = true;
    // The type is a hint the server checks before reading 40 MB; the filename
    // rides in a header because the body IS the file.
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('X-Filename', encodeURIComponent(file.name).slice(0, 120));

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onUploadProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: any = {};
      try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON error page */ }
      if (xhr.status >= 200 && xhr.status < 300 && body.id) {
        onUploadProgress(1);
        resolve(body);
      } else {
        reject(new Error(body.error || `Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('The upload could not be sent.'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });
}

/**
 * Follow a job to its end.
 *
 * `EventSource` rather than a fetch reader: this is a plain GET with no headers
 * to set, and EventSource brings its own reconnect — which is exactly right
 * here, because the job outlives the connection. A phone that sleeps mid-job
 * reconnects and gets the current state on the first event (the backend replays
 * it on subscribe).
 */
export function followTranscription(
  id: string,
  onEvent: (e: TranscribeProgress) => void,
): () => void {
  const source = new EventSource(`${API}/api/transcribe/${id}/events`, { withCredentials: true });
  let settled = false;

  source.onmessage = (msg) => {
    try {
      const event = JSON.parse(msg.data) as TranscribeProgress;
      if (event.stage === 'done' || event.stage === 'error') {
        settled = true;
        source.close();
      }
      onEvent(event);
    } catch { /* a keep-alive comment frame */ }
  };
  source.onerror = () => {
    // EventSource retries by itself; only a failure AFTER the terminal event —
    // or one that never connects — is worth reporting, and the former is just
    // the close we asked for.
    if (settled) return;
    if (source.readyState === EventSource.CLOSED) {
      onEvent({ stage: 'error', done: 0, total: 0, text: '', seconds: 0, error: 'The connection dropped.' });
    }
  };

  return () => { settled = true; source.close(); };
}

export async function cancelTranscription(id: string): Promise<void> {
  await fetch(`${API}/api/transcribe/${id}/cancel`, { method: 'POST', credentials: 'include' }).catch(() => {});
}

/**
 * One dictated phrase, transcribed.
 *
 * The mic button's fallback path, used only where the browser has no speech
 * recognition of its own — see lib/liveSpeech.ts. Plain `fetch` rather than the
 * XHR the file upload uses: a phrase is a few tens of kilobytes, so there is no
 * upload progress worth showing.
 *
 * The thrown error carries a `stop` flag for the one case the caller must treat
 * differently: an allowance that has run out means closing the microphone, not
 * dropping a phrase and carrying on into a wall.
 */
export interface ClipResult {
  text: string;
  /**
   * The language the words turned out to be in, when it could be told from the
   * transcript alone. Absent when it could not — see the server's `detectLocal`.
   */
  lang?: string;
  confidence?: number;
}

export async function transcribeClip(blob: Blob, ext: string, durationMs: number): Promise<ClipResult> {
  const res = await fetch(`${API}/api/transcribe/live`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': blob.type || 'application/octet-stream',
      'X-Audio-Ext': ext,
      'X-Duration-Ms': String(Math.round(durationMs)),
    },
    body: blob,
  });

  readMarqHeader(res);
  let body: any = {};
  try { body = await res.json(); } catch { /* an error page rather than JSON */ }
  if (!res.ok) {
    // A recovered session is not something to apologise for, but this recording
    // is gone either way — the body was consumed by the request that failed —
    // so it says what to do rather than sending anybody to a form they do not
    // need any more.
    const signIn = await recoverOrRedirect(res, body);
    if (signIn === 'retry') throw new Error('That took too long — try again.');
    if (signIn === 'gone') throw new Error('Please sign in.');
    const error = new Error(body.error || `Dictation failed (${res.status})`);
    (error as any).stop = body.stop === true;
    throw error;
  }
  return {
    text: String(body.text ?? ''),
    ...(body.lang ? { lang: String(body.lang), confidence: Number(body.confidence) || 0 } : {}),
  };
}

/* ── Pronunciation ──────────────────────────────────────────────────────────
 *
 * The card under the input box, for one or two words. The server answers
 * `{ ipa: null }` for everything that is not a word rather than an error — see
 * routes/pronounce.ts — so there is exactly one failure path here and it is
 * "no card", never a message.
 */

export interface Pronunciation {
  /** Broad IPA without delimiters; the card draws the slashes. */
  ipa: string;
  /** Latin respelling, present only for text that is not already Latin. */
  roman: string | null;
}

export async function fetchPronunciation(
  text: string,
  lang: string,
  signal?: AbortSignal,
): Promise<Pronunciation | null> {
  const post = (token: string | null) =>
    fetch(`${API}/api/pronounce`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'X-GT-Token': token } : {}),
      },
      body: JSON.stringify({ text, lang }),
      signal,
    });

  let res = await post(currentToken() ?? (await refreshToken()));
  // The same one retry the translate call makes, for the same reason: a tab
  // left open past the token's lifetime should look up a word, not fail.
  if (res.status === 403) {
    const fresh = await refreshToken();
    if (fresh) res = await post(fresh);
  }
  readMarqHeader(res);
  if (!res.ok) {
    // The one failure this must not swallow: a card that quietly does not
    // appear is the right answer to a model hiccup and the wrong answer to
    // "you are signed out", which has a page that fixes it.
    // Recovered or not, this card does not appear this time — it is drawn again
    // on the next keystroke, by which point the session is back.
    if (res.status === 401) await recoverOrRedirect(res, await res.json().catch(() => ({})));
    return null;
  }

  const body = await res.json().catch(() => ({} as any));
  if (!body?.ipa) return null;
  return { ipa: String(body.ipa), roman: body.roman ? String(body.roman) : null };
}

/* ── The dictionary entry ────────────────────────────────────────────────────
 *
 * The other thing that can be asked about a single word, and deliberately a
 * separate call from the pronunciation above rather than one endpoint returning
 * both. They are two different sizes: the phonetics are one line off a small
 * prompt and want to land while the word is still being typed, the entry is a
 * few hundred tokens and wants to land once the typing has stopped. Fused, the
 * fast half would wait for the slow one on every keystroke.
 */

/** One sense of the headword — see the server's lib/dictionary.ts. */
export interface Sense {
  pos: string | null;
  gloss: string;
}

export interface Example {
  /** In the SOURCE language, so it contains the word. */
  text: string;
  translation: string;
}

export interface DictionaryEntry {
  /** Never empty — the server drops an entry with no meaning in it. */
  senses: Sense[];
  explanation: string | null;
  examples: Example[];
}

export async function fetchDictionary(
  text: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<DictionaryEntry | null> {
  const post = (token: string | null) =>
    fetch(`${API}/api/dictionary`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'X-GT-Token': token } : {}),
      },
      body: JSON.stringify({ text, from, to }),
      signal,
    });

  let res = await post(currentToken() ?? (await refreshToken()));
  // The same one retry the translate and pronounce calls make, for the same
  // reason: a tab left open past the token's lifetime should look a word up
  // rather than fail.
  if (res.status === 403) {
    const fresh = await refreshToken();
    if (fresh) res = await post(fresh);
  }
  if (!res.ok) {
    // Same rule as the pronunciation card above.
    // Recovered or not, this card does not appear this time — it is drawn again
    // on the next keystroke, by which point the session is back.
    if (res.status === 401) await recoverOrRedirect(res, await res.json().catch(() => ({})));
    return null;
  }

  const body = await res.json().catch(() => ({}) as any);
  const entry = body?.entry;
  // Shape-checked here as well as on the server, because this is drawn as an
  // authority — a card that says "noun: undefined" is worse than no card.
  if (!entry || !Array.isArray(entry.senses) || !entry.senses.length) return null;

  const senses: Sense[] = entry.senses
    .filter((s: any) => s && typeof s.gloss === 'string' && s.gloss.trim())
    .map((s: any) => ({
      pos: typeof s.pos === 'string' && s.pos.trim() ? s.pos.trim() : null,
      gloss: String(s.gloss).trim(),
    }));
  if (!senses.length) return null;

  const examples: Example[] = Array.isArray(entry.examples)
    ? entry.examples
        .filter(
          (e: any) =>
            e && typeof e.text === 'string' && e.text.trim() &&
            typeof e.translation === 'string' && e.translation.trim(),
        )
        .map((e: any) => ({ text: String(e.text).trim(), translation: String(e.translation).trim() }))
    : [];

  return {
    senses,
    explanation:
      typeof entry.explanation === 'string' && entry.explanation.trim()
        ? entry.explanation.trim()
        : null,
    examples,
  };
}

/* ── Images ─────────────────────────────────────────────────────────────────
 *
 * One call, unlike the voice pair above: reading a photograph is a single
 * upstream request of about a second and a half, so the words come back in the
 * response to the upload itself and there is no job to follow.
 */

export interface ImageLimits {
  enabled: boolean;
  maxBytes: number;
  maxChars: number;
  perDay: number;
  formats: string[];
}

export async function fetchImageLimits(signal?: AbortSignal): Promise<ImageLimits> {
  const res = await fetch(`${API}/api/ocr/limits`, { signal, credentials: 'include' });
  if (!res.ok) throw new Error('limits unavailable');
  return res.json();
}

export interface ImageText {
  /** The writing on the picture, laid out in lines as it was on the page. */
  text: string;
  /** What the reader actually saw, after the server scaled the picture down. */
  width: number;
  height: number;
  truncated: boolean;
  /** Present INSTEAD of text when the picture had nothing written on it. */
  message?: string;
}

/**
 * Send the picture, get the words.
 *
 * XHR rather than fetch for the same one reason `uploadAudio` uses it: upload
 * progress. A photo straight off a phone is a few megabytes, and on a phone
 * connection that upload is most of the wait — `xhr.upload.onprogress` is still
 * the only way to show it moving.
 *
 * The Blob type rather than File, because this also takes a picture pasted out
 * of the clipboard, which arrives with no name attached.
 */
export function readImage(
  file: Blob,
  onUploadProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<ImageText> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/ocr`);
    xhr.withCredentials = true;
    // The type is a hint the server checks before reading megabytes; the name
    // rides in a header because the body IS the picture.
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('X-Filename', encodeURIComponent((file as File).name || 'image').slice(0, 120));

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onUploadProgress(e.loaded / e.total);
    };
    // The dialog switches from "sending" to "reading" on a fraction of 1, and
    // `onprogress` does not promise to deliver one: a small screenshot over a
    // fast link can finish in a single non-computable event. This is the event
    // that always fires when the last byte has gone.
    xhr.upload.onload = () => onUploadProgress(1);
    xhr.onload = () => {
      let body: any = {};
      try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON error page */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        onUploadProgress(1);
        resolve({
          text: String(body.text ?? ''),
          width: Number(body.width) || 0,
          height: Number(body.height) || 0,
          truncated: body.truncated === true,
          ...(body.message ? { message: String(body.message) } : {}),
        });
      } else {
        reject(new Error(body.error || `Could not read that image (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('The image could not be sent.'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });
}

// ── Feedback ─────────────────────────────────────────────────────────────────

export interface FeedbackPayload {
  message: string;
  /** Set only when one of the two faces was pressed. */
  sentiment?: 'good' | 'bad' | null;
  email?: string | null;
  /**
   * The panes as they stood, sent only when the sender ticked the box that says
   * so. "This is wrong" with the two texts attached is a bug report; without
   * them it is a mood, and the server stores nothing it was not offered.
   */
  sourceLang?: string | null;
  targetLang?: string | null;
  mode?: string | null;
  sourceText?: string | null;
  targetText?: string | null;
  page?: string | null;
}

export async function sendFeedback(payload: FeedbackPayload): Promise<void> {
  const res = await fetch(`${API}/api/feedback`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  let body: any = {};
  try { body = await res.json(); } catch { /* an error page rather than JSON */ }
  // The message matters more here than anywhere else on the page: somebody who
  // has just typed a paragraph and been told "something went wrong" has lost the
  // paragraph as far as they are concerned. The server's own words are shown,
  // and the dialog keeps the text in the box either way.
  if (!res.ok) {
    // The dialog keeps the text in the box, so "try again" is a real
    // instruction here and the paragraph is not lost.
    const signIn = await recoverOrRedirect(res, body);
    if (signIn === 'retry') throw new Error('That took too long — send it again.');
    if (signIn === 'gone') throw new Error('Please sign in.');
    throw new Error(body.error || `Could not send that (${res.status})`);
  }
}

// ── Identity ─────────────────────────────────────────────────────────────────

export interface IdentityState {
  /** True when this person is still going by a generated name — ask them. */
  needsName: boolean;
  /** What they answered before, when they have. */
  nickname: string | null;
  team: number | null;
  /** The company they gave with it, if they gave one. Null for most visitors. */
  company: string | null;
  /** The teams that exist. Sent by the server so the buttons cannot drift from it. */
  teams: number[];
  maxNickname: number;
  maxCompany: number;
  /**
   * What the company box is pre-filled with before anybody has answered.
   *
   * From the server, like `noTeamNickname` below and for the same reason: the
   * value shown to the person and the value the product means by "the company
   * everyone here belongs to" have to be one string, not two copies of it.
   */
  defaultCompany: string;
  /**
   * The one nickname everybody on team 0 is saved under.
   *
   * Sent rather than hardcoded here, because the dialog LOCKS the box to this
   * string and the server writes its own copy of it regardless of what the
   * request carried. Two places holding the same constant is two places that can
   * drift, and the drift would only surface when somebody compared the dialog
   * with the console.
   */
  noTeamNickname: string | null;
  /**
   * This visitor's own avatar, as the URL that renders it, or null.
   *
   * A URL rather than the bytes: the same public route the admin console reads
   * from, so there is one way an avatar is fetched and one thing to cache.
   */
  avatarUrl: string | null;
  /** An operator has switched the identity prompt off in the console. */
  disabled: boolean;
  /**
   * The server could not check — no database, or the lookup threw.
   *
   * Distinct from "they have no nickname", and the distinction is the whole
   * reason it is carried: both arrive as an empty `nickname`, but only one of
   * them is a fact about the person. Nothing nags anybody over the other.
   */
  unavailable: boolean;
}

/**
 * Does this visitor still need to tell us who they are?
 *
 * Asked on every load of the translator, and the SERVER decides — not the
 * browser. The answer is also cached in `localStorage`, but only to pre-fill the
 * boxes if we ever ask again; a mandatory dialog whose trigger lives somewhere
 * the user can clear is not mandatory.
 *
 * Never throws. A failure here must not stop the page from rendering, and the
 * safe reading of "we could not find out" is "do not wall anybody in" — the same
 * decision the server makes when its database is unreachable.
 */
export async function fetchIdentity(signal?: AbortSignal): Promise<IdentityState | null> {
  try {
    const res = await fetch(`${API}/api/identity`, { credentials: 'include', signal });
    if (!res.ok) return null;
    const body = await res.json();
    return {
      needsName: !!body.needsName,
      nickname: body.nickname ?? null,
      team: typeof body.team === 'number' ? body.team : null,
      company: typeof body.company === 'string' ? body.company : null,
      teams: Array.isArray(body.teams) && body.teams.length ? body.teams : [0, 1, 2, 3, 4],
      maxNickname: typeof body.maxNickname === 'number' ? body.maxNickname : 40,
      maxCompany: typeof body.maxCompany === 'number' ? body.maxCompany : 24,
      defaultCompany:
        typeof body.defaultCompany === 'string' && body.defaultCompany
          ? body.defaultCompany
          : DEFAULT_COMPANY,
      noTeamNickname: typeof body.noTeamNickname === 'string' ? body.noTeamNickname : null,
      avatarUrl: typeof body.avatarKey === 'string' ? `${API}/api/avatar/${body.avatarKey}` : null,
      disabled: !!body.disabled,
      unavailable: !!body.unavailable,
    };
  } catch {
    return null;
  }
}

/**
 * The URL that renders an avatar key, or null for anything that is not one.
 *
 * Exported so a reader that only holds a key — the account from /api/auth/me,
 * or the `kharma:avatar` event the Tiger editor fires after a save — builds the
 * same URL as everything above, against the same API base.
 */
export function avatarSrc(key: unknown): string | null {
  return typeof key === 'string' && /^[0-9a-f]{32}$/.test(key) ? `${API}/api/avatar/${key}` : null;
}

/**
 * Replace this visitor's avatar. Returns the URL the new image lives at.
 *
 * RAW BYTES, not FormData — the route is `express.raw` and there is exactly one
 * file with no other fields, so a multipart envelope would be pure overhead on
 * both ends. The Blob's own type becomes the Content-Type, which is the thing
 * the server validates the magic number against.
 */
export async function uploadAvatar(file: Blob): Promise<{ avatarUrl: string }> {
  const res = await fetch(`${API}/api/identity/avatar`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  let body: any = {};
  try { body = await res.json(); } catch { /* an error page rather than JSON */ }
  if (!res.ok) throw new Error(body.error || `Could not upload that (${res.status})`);
  return { avatarUrl: `${API}/api/avatar/${body.avatarKey}` };
}

/** Remove it again. "No avatar" afterwards is the same state as never having one. */
export async function removeAvatar(): Promise<void> {
  const res = await fetch(`${API}/api/identity/avatar`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let body: any = {};
    try { body = await res.json(); } catch { /* ignore */ }
    throw new Error(body.error || `Could not remove that (${res.status})`);
  }
}

/**
 * Send the answer. Throws with the server's own words on failure, because the
 * dialog cannot be closed and the person is looking at the only message they are
 * going to get.
 */
export async function claimIdentity(
  nickname: string, team: number, company?: string | null,
): Promise<{ name: string }> {
  const res = await fetch(`${API}/api/identity`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    // Only when there is one: this is the anonymous path, the public dialog
    // has never asked, and an empty string would be a company nobody typed.
    body: JSON.stringify(company ? { nickname, team, company } : { nickname, team }),
  });
  let body: any = {};
  try { body = await res.json(); } catch { /* an error page rather than JSON */ }
  if (!res.ok) throw new Error(body.error || `Could not save that (${res.status})`);
  return { name: body.name ?? nickname };
}

// ── Accounts ─────────────────────────────────────────────────────────────────

export interface Account {
  id: string;
  /** Unique, chosen by them, and the thing their work is now recorded under. */
  nickname: string;
  team: number;
  /**
   * Who they work for. With `team`, this is how they are labelled — `Shark-2`.
   *
   * Always a string: the server applies the legacy default for accounts that
   * predate the field (see its lib/identity.ts), and `fetchAuth` below applies
   * it again for a backend that predates it entirely. Nothing downstream has
   * to handle a missing company, which is the point — the label is drawn in
   * the header, the profile dialog, the team page and four console tables.
   */
  company: string;
  /**
   * What they are allowed to do. See lib/roles.ts for the ladder.
   *
   * Here so the interface can draw itself before asking anything — whether to
   * offer the team view, whether to show a link to the console. It is never a
   * permission: every route checks the role again server-side, against the
   * database rather than against this.
   */
  role: Role;
  /**
   * Country flags this person has switched off — Settings ▸ Flags.
   *
   * The codes that are HIDDEN, not the ones that are shown, for the reason the
   * backend's models/User.ts gives: the language registry keeps gaining
   * countries, and only an exclusion list leaves the new ones visible.
   *
   * It rides on the account rather than living in `localStorage` beside the
   * language tabs because it is a statement about the product, not about this
   * browser — somebody who does not want a flag on their screen does not want it
   * on their phone either. It also means it arrives with the answer the app
   * already waits for at boot (`/api/auth/me`), so nothing flashes the wrong
   * flag while a second request is in the air.
   */
  flagsOff: string[];
  /** 'male' | 'female'; null only for a new Reddit account on its welcome step. */
  gender: Gender | null;
  /** How the account signs in. A Reddit account has no password. */
  authProvider: 'password' | 'reddit';
  /** The account's picture (its Tiger, or an uploaded photo) — render with avatarSrc(); null = none. */
  avatarKey: string | null;
}

export type Gender = 'male' | 'female';

export interface GuestCredits {
  total: number;
  used: number;
  remaining: number;
}

export interface AuthState {
  /** null when this browser is not signed in — a normal answer, not a failure. */
  user: Account | null;
  teams: number[];
  maxNickname: number;
  maxCompany: number;
  /** What the registration form pre-fills the company box with. From the server. */
  defaultCompany: string;
  minPassword: number;
  /** "Join with Reddit" is configured on this server. */
  reddit: boolean;
  /**
   * Free translations for a signed-out browser — null for an account. A
   * `total` of 0 means guest mode is off (sign-in required, as before).
   */
  guest: GuestCredits | null;
  /**
   * The KHARMA wallet — for an account only, and null when KHARMA is switched off
   * server-side or when talking to a build that predates it.
   */
  marq: MarqWallet | null;
  /**
   * The question could not be asked — the backend is down or unreachable.
   *
   * Kept apart from `user: null` for the same reason IdentityState.unavailable
   * is kept apart from an empty nickname: one of them means "this person is not
   * signed in" and the other means "we do not know", and sending somebody to the
   * login page over a failed fetch would sign them out of a working session
   * every time the API hiccupped.
   */
  unavailable: boolean;
}

/** Shared by every call here: the server's own sentence, or a fallback. */
async function authBody(res: Response, fallback: string): Promise<any> {
  let body: any = {};
  try { body = await res.json(); } catch { /* an error page rather than JSON */ }
  if (!res.ok) throw new Error(body.error || `${fallback} (${res.status})`);
  return body;
}

/**
 * Is this browser signed in, and as whom?
 *
 * Never throws: a network failure comes back as `unavailable`, which the gate
 * treats as "wait" rather than as "throw them out".
 */
export async function fetchAuth(signal?: AbortSignal): Promise<AuthState> {
  try {
    // Cookies carry the sign-in. A legacy stored token (if any) rides along ONCE, to be converted.
    const res = await fetch(`${API}/api/auth/me`, { credentials: 'include', signal, headers: authHeaders(), cache: 'no-store' });
    if (!res.ok) throw new Error('unavailable');
    const body = await res.json();
    // A legacy stored token rode along once as Bearer; the server has turned it into cookies (or refused it). Erase it.
    if (body.user) rememberToken(null, body.user.nickname);
    else if (readToken()) dropToken();
    return {
      // Normalised here as well as on the server, because this is also the
      // path that talks to an older backend: a build that predates the flag
      // setting answers without the field, and every reader downstream treats
      // "absent" as "hide nothing" rather than as a crash.
      user: body.user
        ? {
            ...body.user,
            flagsOff: body.user.flagsOff ?? [],
            // Same reason as `flagsOff`: this is also the path that talks to an
            // older backend, and every screen composes a label out of it.
            company: companyOf(body.user.company),
            gender: body.user.gender === 'male' || body.user.gender === 'female' ? body.user.gender : null,
            authProvider: body.user.authProvider === 'reddit' ? 'reddit' : 'password',
            avatarKey: typeof body.user.avatarKey === 'string' ? body.user.avatarKey : null,
          }
        : null,
      teams: Array.isArray(body.teams) && body.teams.length ? body.teams : [0, 1, 2, 3, 4],
      maxNickname: typeof body.maxNickname === 'number' ? body.maxNickname : 40,
      maxCompany: typeof body.maxCompany === 'number' ? body.maxCompany : 24,
      defaultCompany:
        typeof body.defaultCompany === 'string' && body.defaultCompany
          ? body.defaultCompany
          : DEFAULT_COMPANY,
      minPassword: typeof body.minPassword === 'number' ? body.minPassword : 8,
      reddit: body.reddit === true,
      guest:
        !body.user && body.guest && typeof body.guest.total === 'number'
          ? {
              total: body.guest.total,
              used: Number(body.guest.used) || 0,
              remaining: Number(body.guest.remaining) || 0,
            }
          : null,
      /*
       * Absent on an older backend, and absent for a signed-out browser, and
       * both read as null rather than as a zero balance — a chip showing "0
       * KHARMA" to somebody who simply has no wallet would be a lie that invites
       * a support ticket. The header draws nothing at all when this is null.
       */
      marq:
        body.user && body.marq && typeof body.marq.balance === 'number' && body.marq.enabled !== false
          ? {
              balance: Number(body.marq.balance) || 0,
              canClaimDaily: !!body.marq.canClaimDaily,
              nextDailyAt: String(body.marq.nextDailyAt || ''),
              dailyAmount: Number(body.marq.dailyAmount) || 0,
              daily: body.marq.daily,
              earned: Number(body.marq.earned) || 0,
              spent: Number(body.marq.spent) || 0,
              enabled: true,
            }
          : null,
      unavailable: false,
    };
  } catch {
    return {
      user: null,
      teams: [0, 1, 2, 3, 4],
      maxNickname: 40,
      maxCompany: 24,
      defaultCompany: DEFAULT_COMPANY,
      minPassword: 8,
      reddit: false,
      guest: null,
      marq: null,
      unavailable: true,
    };
  }
}

export async function registerAccount(
  nickname: string, password: string, gender: Gender, remember = true,
): Promise<Account> {
  // Nickname, password, gender — nothing else (owner 2026-10-12). The server files the team as 0.
  const res = await fetch(`${API}/api/auth/register`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nickname, password, gender, remember }),
  });
  const body = await authBody(res, 'Could not create your account');
  rememberToken(null, body.user?.nickname || nickname);   // the nickname only — the sign-in lives in HttpOnly cookies
  return body.user;
}

export interface TeamMember {
  id: string;
  nickname: string;
  /** Their picture (`/api/avatar/<key>`), null or absent when they have none. */
  avatar?: string | null;
  role: Role;
  translations: number;
  charsTranslated: number;
  lastSeenAt: string | null;
  lastLoginAt: string | null;
  disabled: boolean;
  isYou: boolean;
}

export interface TeamActivityRow {
  id: string;
  who: string;
  whoAvatar?: string | null;
  mode: string;
  modeLabel: string;
  from: string;
  to: string;
  sourceText: string;
  targetText: string;
  createdAt: string;
}

/**
 * One request, as much of it as a team leader may see.
 *
 * Deliberately narrower than the console's log row: no headers, no body, no
 * query string, no address. See the backend's routes/team.ts for the full
 * reasoning — the short version is that a leader gets the SHAPE of their team's
 * work and not its contents.
 */
export interface TeamLogRow {
  id: string;
  who: string;
  method: string;
  path: string;
  status: number;
  ms: number;
  kind: string;
  browser: string | null;
  device: string | null;
  country: string | null;
  countryCode: string | null;
  city: string | null;
  createdAt: string;
}

export interface TeamActivity {
  team: number;
  /** Who the team belongs to, and the finished label — `Shark-2`. */
  company: string;
  tag: string;
  you: { id: string; nickname: string; role: Role };
  members: TeamMember[];
  recent: TeamActivityRow[];
  logs: TeamLogRow[];
}

/**
 * The caller's own team and what it has been doing — a team leader's one
 * privilege beyond an ordinary account.
 *
 * No parameters, and that is the security of it: the team comes from the signed
 * -in account server-side. See the backend's routes/team.ts.
 */
export async function fetchTeam(): Promise<TeamActivity> {
  const res = await fetch(`${API}/api/team`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any)?.error ?? 'Could not load your team.');
  }
  return (await res.json()) as TeamActivity;
}

export async function loginAccount(nickname: string, password: string, remember = true): Promise<Account> {
  const res = await fetch(`${API}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    // every sign-in is kept: a 180-day sliding refresh cookie (backend lib/auth.ts)
    body: JSON.stringify({ nickname, password, remember }),
  });
  const body = await authBody(res, 'Could not sign you in');
  rememberToken(null, body.user?.nickname || nickname);   // the nickname only — the sign-in lives in HttpOnly cookies
  return body.user;
}

export async function logoutAccount(): Promise<void> {
  await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
  forgetToken();   // and the old password copy, if one was still here
}

/**
 * Change the nickname, the company and/or the team on the account itself.
 *
 * What the profile dialog now writes to. It used to POST /api/identity, which
 * wrote the answer onto the IP's visitor document — so the same two boxes moved
 * a label attached to a network rather than to a person.
 */
export async function updateAccount(
  patch: { nickname?: string; team?: number; company?: string; gender?: Gender },
): Promise<Account> {
  const res = await fetch(`${API}/api/auth/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return (await authBody(res, 'Could not save that')).user;
}

/**
 * Replace the whole list of hidden flags.
 *
 * The WHOLE list, every time — see the backend route for why a per-flag toggle
 * would be order-dependent over a network that reorders.
 */
export async function saveFlagsOff(flagsOff: string[]): Promise<Account> {
  const res = await fetch(`${API}/api/auth/flags`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ flagsOff }),
  });
  return (await authBody(res, 'Could not save your flag settings')).user;
}
